import { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Container, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockIcon from "@mui/icons-material/Block";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PasswordSetupAPI from "../../api/PasswordSetupAPI";

const LOGO_URL = "https://allyhealthgroup.s3.us-east-1.amazonaws.com/uploads/2025/01/Ally-3-copy-2.png";
const token = new URLSearchParams(window.location.search).get("t");

const StatusPage = ({ iconBg, Icon, iconColor, title, message, children }) => (
	<Box
		sx={{
			minHeight: "70vh",
			bgcolor: "#EBF5FF",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			py: { xs: 4, md: 8 },
		}}
	>
		<Container maxWidth="sm">
			<Paper
				elevation={0}
				sx={{
					borderRadius: 4,
					overflow: "hidden",
					boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
				}}
			>
				<Box
					sx={{
						bgcolor: "#EBF5FF",
						py: 3,
						px: 4,
						textAlign: "center",
					}}
				>
					<img src={LOGO_URL} alt="Avocado" style={{ maxWidth: 90 }} />
				</Box>
				<Stack spacing={3} sx={{ p: 5, textAlign: "center", alignItems: "center" }}>
					<Box
						sx={{
							width: 72,
							height: 72,
							borderRadius: "50%",
							bgcolor: iconBg,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: `0 0 0 8px ${iconBg}`,
						}}
					>
						<Icon sx={{ fontSize: 38, color: iconColor }} />
					</Box>
					<Box>
						<Typography variant="h5" fontWeight={700} color="text.primary" mb={1}>
							{title}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 360, mx: "auto" }}>
							{message}
						</Typography>
					</Box>
					{children}
				</Stack>
			</Paper>
		</Container>
	</Box>
);

const SetPasswordPage = () => {
	const [status, setStatus] = useState("loading");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [alert, setAlert] = useState(null);
	const [passwordsMatch, setPasswordsMatch] = useState(true);

	useEffect(() => {
		if (!token) {
			setStatus("missing");
			return;
		}

		PasswordSetupAPI.validateToken(token)
			.then((data) => {
				setStatus(data?.user_id ? "valid" : "invalid");
			})
			.catch(() => setStatus("invalid"));
	}, []);

	useEffect(() => {
		if (confirm !== "") {
			setPasswordsMatch(password === confirm);
		}
	}, [password, confirm]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setAlert(null);

		if (password !== confirm) {
			setAlert({ severity: "error", message: "Passwords do not match." });
			return;
		}

		if (password.length < 8) {
			setAlert({
				severity: "error",
				message: "Password must be at least 8 characters.",
			});
			return;
		}

		setSubmitting(true);

		try {
			const data = await PasswordSetupAPI.setPassword(token, password);

			if (data?.success) {
				setStatus("success");
				setTimeout(() => {
					window.location.href = window.location.origin + "/login";
				}, 3000);
			} else {
				setAlert({
					severity: "error",
					message: data?.message || "Something went wrong. Please try again.",
				});
			}
		} catch {
			setAlert({
				severity: "error",
				message: "Something went wrong. Please try again.",
			});
		} finally {
			setSubmitting(false);
		}
	};

	if (status === "loading") {
		return (
			<Box
				sx={{
					minHeight: "70vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "#EBF5FF",
				}}
			>
				<Stack alignItems="center" spacing={2}>
					<CircularProgress size={48} thickness={3} sx={{ color: "#5BB8F5" }} />
					<Typography variant="body2" color="text.secondary" letterSpacing={0.5}>
						Verifying your link...
					</Typography>
				</Stack>
			</Box>
		);
	}

	if (status === "success") {
		return (
			<StatusPage
				iconBg="#ECFFF7"
				Icon={CheckCircleIcon}
				iconColor="#19A874"
				title="Password Set"
				message="Your password has been saved successfully. Redirecting you to login..."
			/>
		);
	}

	if (status === "missing") {
		return (
			<StatusPage
				iconBg="#fef2f2"
				Icon={BlockIcon}
				iconColor="#e53935"
				title="Invalid Link"
				message="This password setup link is invalid. Please check the link in your email and try again."
			/>
		);
	}

	if (status !== "valid") {
		return (
			<StatusPage
				iconBg="#fef2f2"
				Icon={ErrorOutlineIcon}
				iconColor="#e53935"
				title="Unable to Continue"
				message="This link is no longer valid. Please ask for a new password setup email."
			/>
		);
	}

	return (
		<Box sx={{ minHeight: "70vh", bgcolor: "#EBF5FF", py: { xs: 4, md: 8 } }}>
			<Container maxWidth="md">
				<Paper
					elevation={0}
					sx={{
						borderRadius: 4,
						overflow: "hidden",
						maxWidth: 820,
						mx: "auto",
						border: "1px solid #A0D8FA",
						boxShadow: "0 8px 30px rgba(58,125,201,.08)",
					}}
				>
					<Stack direction={{ xs: "column", md: "row" }} sx={{ minHeight: 480 }}>
						{/* LEFT PANEL - dark brand */}
						<Box
							sx={{
								width: { xs: "100%", md: 280 },
								bgcolor: "#F8FCFF",
								borderRight: "1px solid #A0D8FA",
								p: 4,
								display: "flex",
								flexDirection: "column",
								justifyContent: "space-between",
							}}
						>
							<Box>
								<img src={LOGO_URL} alt="AHG" style={{ maxWidth: 110, marginBottom: 32 }} />
								<Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
									<LockOutlinedIcon sx={{ fontSize: 18, color: "#3A7DC9" }} />
									<Typography variant="subtitle1" fontWeight={700} color="#1A3F7A">
										Account Setup
									</Typography>
								</Stack>
								<Typography variant="body2" sx={{ color: "#1A3F7A", lineHeight: 1.8 }}>
									Choose a strong new password to secure your account.
								</Typography>
							</Box>

							<Box mt={4}>
								<Typography
									variant="caption"
									sx={{
										color: "#1A3F7A",
										textTransform: "uppercase",
										letterSpacing: 1,
									}}
								>
									Requirements
								</Typography>
								<Stack spacing={0.75} mt={1}>
									{["Minimum 8 characters"].map((req) => (
										<Stack key={req} direction="row" spacing={1} alignItems="center">
											<Box
												sx={{
													width: 5,
													height: 5,
													borderRadius: "50%",
													bgcolor: "#5BB8F5",
													flexShrink: 0,
												}}
											/>
											<Typography variant="caption" sx={{ color: "#4A6A8A" }}>
												{req}
											</Typography>
										</Stack>
									))}
								</Stack>
							</Box>
						</Box>

						{/* RIGHT PANEL - form */}
						<Box
							sx={{
								flex: 1,
								bgcolor: "#1A3F7A",
								p: { xs: 3, md: 5 },
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
							}}
						>
							<Box mb={4}>
								<Typography variant="h5" fontWeight={700} color="#FFFFFF" lineHeight={1.2}>
									Set your password
								</Typography>
								<Typography variant="body2" color="#FFFFFF" mt={0.75}>
									Choose a strong password to secure your account.
								</Typography>
							</Box>

							<form onSubmit={handleSubmit}>
								{alert && (
									<Alert
										severity={alert.severity}
										onClose={() => setAlert(null)}
										sx={{
											mb: 3,
											borderRadius: 2,

											"& .MuiAlert-action .MuiIconButton-root": {
												backgroundColor: "transparent !important",
												color: "inherit",

												"&:hover": {
													backgroundColor: "rgba(0,0,0,0.04) !important",
												},
											},
										}}
									>
										{alert.message}
									</Alert>
								)}

								<Stack spacing={2.5} mb={3}>
									<Box>
										<Typography
											variant="caption"
											fontWeight={600}
											color="#FFFFFF"
											sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
										>
											New Password
										</Typography>
										<Box
											sx={{
												mt: 0.75,
												display: "flex",
												alignItems: "center",
												border: "1px solid #D6E6F5",
												borderRadius: 2,
												px: 1.5,
												height: 40,
												bgcolor: "#1A3F7A",
												transition: "border-color 0.2s",
												"&:hover": { borderColor: "#5BB8F5" },
												"&:focus-within": {
													borderColor: "#5BB8F5",
													borderWidth: 2,
												},
												"& input::-ms-reveal": { display: "none" },
												"& input::-ms-clear": { display: "none" },
											}}
										>
											<input
												type={showPassword ? "text" : "password"}
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												autoComplete="new-password"
												style={{
													flex: 1,
													border: "none",
													outline: "none",
													background: "transparent",
													fontSize: "0.875rem",
													color: "#FFFFFF",
													padding: 0,
												}}
											/>
											<IconButton
												onClick={() => setShowPassword((v) => !v)}
												edge="end"
												size="small"
												sx={{
													backgroundColor: "transparent !important",
													color: "#D6E6F5",
													ml: 0.5,
												}}
											>
												{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
											</IconButton>
										</Box>
									</Box>

									<Box>
										<Typography
											variant="caption"
											fontWeight={600}
											color="#FFFFFF"
											sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
										>
											Confirm Password
										</Typography>
										<Box
											sx={{
												mt: 0.75,
												display: "flex",
												alignItems: "center",
												border: `1px solid ${!passwordsMatch && confirm !== "" ? "#d32f2f" : "#D6E6F5"}`,
												borderRadius: 2,
												px: 1.5,
												height: 40,
												bgcolor: "#1A3F7A",
												transition: "border-color 0.2s",
												"&:hover": {
													borderColor: !passwordsMatch && confirm !== "" ? "#d32f2f" : "#5BB8F5",
												},
												"&:focus-within": {
													borderColor: !passwordsMatch && confirm !== "" ? "#d32f2f" : "#5BB8F5",
													borderWidth: 2,
												},
												"& input::-ms-reveal": { display: "none" },
												"& input::-ms-clear": { display: "none" },
											}}
										>
											<input
												type={showConfirm ? "text" : "password"}
												value={confirm}
												onChange={(e) => setConfirm(e.target.value)}
												autoComplete="new-password"
												style={{
													flex: 1,
													border: "none",
													outline: "none",
													background: "transparent",
													fontSize: "0.875rem",
													color: "#FFFFFF",
													padding: 0,
												}}
											/>
											<IconButton
												onClick={() => setShowConfirm((v) => !v)}
												edge="end"
												size="small"
												sx={{
													backgroundColor: "transparent !important",
													color: showConfirm ? "#FFFFFF" : "#D6E6F5",
													ml: 0.5,
												}}
											>
												{showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
											</IconButton>
										</Box>
										{!passwordsMatch && confirm !== "" && (
											<Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
												Passwords do not match
											</Typography>
										)}
									</Box>
								</Stack>

								<Button
									type="submit"
									variant="contained"
									fullWidth
									disabled={submitting || !passwordsMatch || password === "" || confirm === ""}
									sx={{
										height: 46,
										backgroundColor: "#EBF5FF !important",
										color: "#1A3F7A",
										textTransform: "none",
										fontSize: "0.95rem",
										fontWeight: 600,
										borderRadius: 2.5,
										boxShadow: "none",
										letterSpacing: 0.3,
										"&:hover": {
											backgroundColor: "#3A7DC9 !important",
											color: "#FFFFFF",
											boxShadow: "0 4px 12px rgba(63,73,38,0.3)",
										},
										"&:disabled": { bgcolor: "#D6E6F5", color: "#1A3F7A" },
									}}
								>
									{submitting ? <CircularProgress size={22} color="inherit" /> : "Set Password"}
								</Button>

								<Stack direction="row" alignItems="center" spacing={1} mt={2.5}>
									<ShieldOutlinedIcon sx={{ fontSize: 14, color: "#D6E6F5" }} />
									<Typography variant="caption" color="#D6E6F5">
										Your password will be saved once you complete this form.
									</Typography>
								</Stack>
							</form>
						</Box>
					</Stack>
				</Paper>
			</Container>
		</Box>
	);
};

export default SetPasswordPage;
